"use client";

import { motion, useReducedMotion } from "framer-motion";
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  type LucideIcon,
  Mail,
  Phone,
  Sparkles,
  Voicemail,
} from "lucide-react";
import Image from "next/image";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Avatar } from "@/ui/elements";

const EASE = [0.16, 1, 0.3, 1] as const;
const MARIANA_TEK = "/images/integrations/mariana-tek/icon.png";
const SCOTT = { name: "Scott Tinari", src: "/images/website/customers/scott-tinari.jpg" };

/* ================================================================== */
/* Thread card — the omnichannel conversation                         */
/* ================================================================== */

function Bubble({
  direction,
  children,
}: {
  direction: "incoming" | "outgoing";
  children: ReactNode;
}) {
  const out = direction === "outgoing";
  return (
    <div className={cn("w-full", out ? "text-right" : "text-left")}>
      <div
        className={cn(
          "inline-block max-w-[15rem] whitespace-pre-line rounded-2xl border px-3 py-2 text-left text-[13px] leading-snug",
          out
            ? "rounded-br-md border-transparent bg-outgoing-bubble text-white"
            : "rounded-bl-md border-border bg-incoming-bubble text-foreground",
        )}
      >
        {children}
      </div>
    </div>
  );
}

// Centered event pill — the homepage-scene style for channel events that aren't
// chat bubbles (calls, voicemails, form submissions).
function EventChip({ icon: Icon, title, sub }: { icon: LucideIcon; title: string; sub?: string }) {
  return (
    <div className="flex justify-center">
      <span className="inline-flex max-w-full flex-wrap items-center justify-center gap-x-1.5 rounded-2xl border border-border bg-background px-2.5 py-1 text-center text-[11px] shadow-sm">
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-medium text-foreground">
          <Icon className="h-3 w-3 shrink-0 text-muted-foreground" />
          {title}
        </span>
        {sub ? <span className="whitespace-nowrap text-muted-foreground">· {sub}</span> : null}
      </span>
    </div>
  );
}

// Centered system line — for AI insights / transcripts.
function SystemLine({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center justify-center gap-1.5 px-6 text-center text-[11px] italic text-muted-foreground">
      <Sparkles className="h-3 w-3 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

function OutgoingEmail({
  subject,
  preview,
}: {
  subject: string;
  preview: string;
}) {
  return (
    <div className="flex justify-end">
      <div className="w-[15rem] overflow-hidden rounded-2xl rounded-br-md bg-outgoing-bubble text-left text-white shadow-sm">
        <div className="flex items-center gap-1.5 bg-black/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider">
          <Mail className="size-3" />
          Email
        </div>
        <div className="px-3 py-2.5">
          <p className="text-[13px] font-bold leading-tight">{subject}</p>
          <p className="mt-1.5 line-clamp-2 text-[12px] leading-snug text-white/90">{preview}</p>
        </div>
      </div>
    </div>
  );
}

export function ThreadCard({
  className,
  showComposer = true,
}: {
  className?: string;
  showComposer?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden rounded-2xl border border-border bg-background",
        className,
      )}
    >
      {/* Header: contact */}
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <Avatar name={SCOTT.name} src={SCOTT.src} size="xs" />
        <span className="truncate text-[13px] font-semibold text-foreground">{SCOTT.name}</span>
        <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
      </div>

      {/* Messages — every channel in one thread, anchored to newest */}
      <div className="flex min-h-0 flex-1 flex-col justify-end gap-3 overflow-hidden px-4 py-3">
        <EventChip icon={ClipboardList} title="Form submitted" sub="Guest pass request" />
        <Bubble direction="incoming">Hey! Can I bring my daughter to Saturday's 9 AM?</Bubble>
        <EventChip icon={Phone} title="Call" sub="3m 20s" />
        <EventChip icon={Voicemail} title="Voicemail" sub="0:25" />
        <SystemLine>AI summary: bringing a guest to Saturday's class</SystemLine>
        <Bubble direction="outgoing">
          Done, Scott! Saved a guest spot for Saturday's 9 AM. That's 615 classes for you, she's in
          great company 💪
        </Bubble>
        <OutgoingEmail
          subject="Your guest pass for Saturday"
          preview="Forward this to reserve her spot in the 9 AM class and grab free parking out back…"
        />
        <Bubble direction="incoming">Perfect, thank you! See you Saturday 🙌</Bubble>
      </div>

      {/* Composer */}
      {showComposer ? (
        <div className="border-t border-border p-3">
          <div className="rounded-xl border border-border px-3 py-2.5">
            <p className="text-[13px] text-muted-foreground">Enter text message...</p>
            <div className="mt-4 flex items-center justify-end">
              <span className="rounded-lg bg-primary px-4 py-1.5 text-[13px] font-semibold text-primary-foreground">
                Send
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ================================================================== */
/* Contact card — the context differentiator (animated)               */
/* ================================================================== */

const TABS = ["Details", "Activity", "Membership"];

function ChevValue({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {children}
      <ChevronRight className="size-3 text-muted-foreground" />
    </span>
  );
}

export function ContactCard({ className }: { className?: string }) {
  const reduce = useReducedMotion();

  const container = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.05, delayChildren: 0.55 } },
  };
  const item = {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE } },
  };
  const iv = reduce ? undefined : item;
  const containerProps = reduce
    ? {}
    : { initial: "hidden" as const, animate: "visible" as const, variants: container };

  function Row({ label, value }: { label: string; value: ReactNode }) {
    return (
      <motion.div variants={iv} className="flex items-center justify-between gap-3">
        <span className="shrink-0 text-[12px] text-muted-foreground">{label}</span>
        <span className="min-w-0 truncate text-[12px] font-medium text-foreground">{value}</span>
      </motion.div>
    );
  }

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-border bg-background shadow-2xl shadow-black/10 dark:shadow-black/50",
        className,
      )}
    >
      <div className="flex items-center gap-1 border-b border-border px-3 py-2.5">
        {TABS.map((tab, i) => (
          <span
            key={tab}
            className={cn(
              "rounded-md px-2.5 py-1 text-[12px] font-medium",
              i === 0 ? "bg-muted text-foreground" : "text-muted-foreground",
            )}
          >
            {tab}
          </span>
        ))}
      </div>

      <motion.div className="space-y-3 p-4" {...containerProps}>
        <motion.div variants={iv} className="flex flex-col items-center">
          <Avatar name={SCOTT.name} src={SCOTT.src} size="lg" />
          <p className="mt-2 text-[13px] font-semibold text-foreground">{SCOTT.name}</p>
          <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-medium text-success">
            <CheckCircle2 className="size-3" />
            Subscribed
          </span>
        </motion.div>

        <div className="space-y-2 border-t border-border pt-3">
          <Row
            label="Source"
            value={
              <span className="inline-flex items-center gap-1.5">
                <Image src={MARIANA_TEK} width={28} height={28} alt="" className="size-3.5 rounded-[3px]" />
                Mariana Tek
              </span>
            }
          />
          <Row label="Phone" value="(845) 774-6921" />
          <Row label="Email" value="sjtinari@gmail.com" />
          <Row label="Date added" value="10/7/2022" />
          <Row label="Birthday" value="Mar 1" />
          <Row label="Age" value="42" />
          <Row
            label="Membership"
            value={
              <ChevValue>
                <span className="inline-flex items-center gap-1.5">
                  <span className="size-1.5 rounded-full bg-success" />
                  Active
                </span>
              </ChevValue>
            }
          />
        </div>

        <motion.div variants={iv} className="rounded-xl border border-border p-3">
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-foreground">
              <Image src={MARIANA_TEK} width={28} height={28} alt="" className="size-3.5 rounded-[3px]" />
              Mariana Tek
            </span>
            <ChevronDown className="size-3.5 text-muted-foreground" />
          </div>
          <div className="mt-2.5 space-y-2 border-t border-border pt-2.5">
            <Row label="Classes completed" value="615" />
            <Row label="Last class" value={<ChevValue>2 days ago</ChevValue>} />
            <Row label="Next class" value={<ChevValue>in 2 days</ChevValue>} />
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
